import { useCallback, useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Plus, Send, Trash2, Pencil, RefreshCw, Cable, Mail } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

function Field({ label, children }) {
    return (
        <label className="flex flex-col gap-2">
            <span
                className="mono text-[10px] uppercase tracking-[0.2em]"
                style={{ color: "var(--text-faint)" }}
            >
                {label}
            </span>
            {children}
        </label>
    );
}

function ChannelForm({ initial, onSubmit, onCancel, submitting }) {
    const [name, setName] = useState(initial?.name || "");
    const [type, setType] = useState(initial?.type || "glip_webhook");
    const [url, setUrl] = useState(initial?.webhook_url || "");
    const [toAddr, setToAddr] = useState(initial?.to_address || "");
    const [enabled, setEnabled] = useState(initial?.enabled ?? true);

    const submit = (e) => {
        e.preventDefault();
        if (!name) {
            toast.error("Name required");
            return;
        }
        if (type === "glip_webhook" && !url) {
            toast.error("Webhook URL required for Glip channels");
            return;
        }
        if (type === "email" && (!toAddr || !toAddr.includes("@"))) {
            toast.error("Valid to_address required for Email channels");
            return;
        }
        const payload = { name, type, enabled };
        if (type === "glip_webhook") payload.webhook_url = url;
        else payload.to_address = toAddr;
        onSubmit(payload);
    };

    return (
        <form onSubmit={submit} className="flex flex-col gap-4">
            <Field label="name">
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="console-input"
                    data-testid="channel-name-input"
                />
            </Field>
            <Field label="channel type">
                <Select value={type} onValueChange={setType}>
                    <SelectTrigger
                        className="console-input"
                        data-testid="channel-type-select"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent
                        className="mono"
                        style={{
                            background: "var(--bg-elev)",
                            border: "1px solid var(--border-strong)",
                            color: "var(--text)",
                        }}
                    >
                        <SelectItem value="glip_webhook">
                            glip_webhook (RC incoming webhook URL)
                        </SelectItem>
                        <SelectItem value="email">
                            email (RC Team post-by-email address)
                        </SelectItem>
                    </SelectContent>
                </Select>
            </Field>
            {type === "glip_webhook" ? (
                <Field label="webhook url">
                    <input
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        className="console-input"
                        data-testid="channel-url-input"
                        placeholder="https://hooks.ringcentral.com/webhook/v2/…"
                    />
                </Field>
            ) : (
                <Field label="to address (RC team email)">
                    <input
                        value={toAddr}
                        onChange={(e) => setToAddr(e.target.value)}
                        className="console-input"
                        data-testid="channel-toaddress-input"
                        placeholder="159451185158@…mvp.ringcentral.com"
                    />
                </Field>
            )}
            <div className="flex items-center justify-between">
                <span
                    className="mono text-[10px] uppercase tracking-[0.2em]"
                    style={{ color: "var(--text-faint)" }}
                >
                    enabled
                </span>
                <Switch
                    checked={enabled}
                    onCheckedChange={setEnabled}
                    data-testid="channel-enabled-switch"
                />
            </div>
            <DialogFooter className="mt-2 gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="ghost-btn"
                    data-testid="channel-cancel-btn"
                >
                    cancel
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="pill-btn"
                    data-testid="channel-save-btn"
                >
                    {submitting ? "saving…" : initial ? "save" : "create"}
                </button>
            </DialogFooter>
        </form>
    );
}

function TypePill({ type }) {
    const isEmail = type === "email";
    return (
        <span
            className="badge"
            style={{
                color: isEmail ? "var(--info)" : "var(--accent)",
                borderColor: isEmail
                    ? "rgba(125,211,252,0.35)"
                    : "rgba(124,255,203,0.35)",
                background: isEmail
                    ? "rgba(125,211,252,0.05)"
                    : "rgba(124,255,203,0.05)",
            }}
            data-testid={`channel-type-${type}`}
        >
            {isEmail ? <Mail size={10} /> : <Cable size={10} />}
            {isEmail ? "email" : "glip"}
        </span>
    );
}

export default function ChannelsPage() {
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/channels");
            setChannels(data.channels || []);
        } catch {
            toast.error("Failed to load channels");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async (payload) => {
        setSaving(true);
        try {
            if (editing?.id) {
                await api.patch(`/channels/${editing.id}`, payload);
                toast.success("Channel updated");
            } else {
                await api.post("/channels", payload);
                toast.success("Channel created");
            }
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const toggleEnabled = async (c) => {
        try {
            await api.patch(`/channels/${c.id}`, { enabled: !c.enabled });
            await load();
        } catch {
            toast.error("Toggle failed");
        }
    };

    const remove = async () => {
        if (!deleting) return;
        try {
            await api.delete(`/channels/${deleting.id}`);
            toast.success("Channel deleted");
            setDeleting(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
            setDeleting(null);
        }
    };

    const runTest = async (c) => {
        const id = toast.loading(`Sending test to ${c.name}…`);
        try {
            const { data } = await api.post(`/channels/${c.id}/test`);
            const desc =
                c.type === "email"
                    ? `mode=${data.mode} · status=${data.status} · code=${data.status_code ?? "—"}`
                    : `mode=${data.mode} · status=${data.status_code ?? "—"}`;
            toast.success(`Test complete: ${desc}`, { id });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test failed", { id });
        }
    };

    return (
        <section className="panel p-6 fade-in" data-testid="channels-panel">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Cable size={16} style={{ color: "var(--accent)" }} />
                    <h2
                        className="mono text-sm uppercase tracking-[0.18em]"
                        style={{ color: "var(--text)" }}
                    >
                        channels
                    </h2>
                    <span
                        className="mono text-xs"
                        style={{ color: "var(--text-faint)" }}
                    >
                        {channels.length}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="ghost-btn"
                        onClick={load}
                        data-testid="channels-refresh-btn"
                    >
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                        refresh
                    </button>
                    <button
                        className="pill-btn"
                        onClick={() => setEditing({})}
                        data-testid="channel-new-btn"
                    >
                        <Plus size={14} /> new channel
                    </button>
                </div>
            </div>

            {channels.length === 0 && !loading && (
                <div
                    className="panel-inset p-10 text-center"
                    data-testid="channels-empty"
                >
                    <div
                        className="mono text-xs uppercase tracking-widest mb-2"
                        style={{ color: "var(--text-faint)" }}
                    >
                        no channels yet
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-2">
                {channels.map((c) => (
                    <div
                        key={c.id}
                        className="panel-inset px-4 py-3 flex items-center gap-3 flex-wrap"
                        data-testid={`channel-row-${c.id}`}
                    >
                        <TypePill type={c.type || "glip_webhook"} />
                        <span
                            className="mono text-sm"
                            style={{ color: "var(--text)", minWidth: 140 }}
                        >
                            {c.name}
                        </span>
                        <span
                            className="mono text-[11px] flex-1 truncate"
                            style={{ color: "var(--text-dim)" }}
                        >
                            {c.destination_masked || "(unset)"}
                        </span>
                        <Switch
                            checked={!!c.enabled}
                            onCheckedChange={() => toggleEnabled(c)}
                            data-testid={`channel-toggle-${c.id}`}
                        />
                        <button
                            className="ghost-btn"
                            onClick={() => runTest(c)}
                            data-testid={`channel-test-btn-${c.id}`}
                        >
                            <Send size={11} /> test
                        </button>
                        <button
                            className="ghost-btn"
                            onClick={() => setEditing(c)}
                            data-testid={`channel-edit-btn-${c.id}`}
                        >
                            <Pencil size={11} /> edit
                        </button>
                        <button
                            className="ghost-btn"
                            onClick={() => setDeleting(c)}
                            data-testid={`channel-delete-btn-${c.id}`}
                            style={{ color: "var(--danger)" }}
                        >
                            <Trash2 size={11} /> delete
                        </button>
                    </div>
                ))}
            </div>

            <Dialog
                open={editing !== null}
                onOpenChange={(o) => !o && setEditing(null)}
            >
                <DialogContent
                    className="mono"
                    style={{
                        background: "var(--bg-elev)",
                        border: "1px solid var(--border-strong)",
                        color: "var(--text)",
                    }}
                    data-testid="channel-dialog"
                >
                    <DialogHeader>
                        <DialogTitle
                            className="mono text-sm uppercase tracking-[0.18em]"
                            style={{ color: "var(--accent)" }}
                        >
                            {editing?.id ? "edit channel" : "new channel"}
                        </DialogTitle>
                        <DialogDescription
                            className="mono text-[11px]"
                            style={{ color: "var(--text-dim)" }}
                        >
                            Glip = RingCentral incoming webhook URL · Email = RC
                            Team post-by-email address
                        </DialogDescription>
                    </DialogHeader>
                    {editing !== null && (
                        <ChannelForm
                            initial={editing?.id ? editing : null}
                            submitting={saving}
                            onSubmit={save}
                            onCancel={() => setEditing(null)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={deleting !== null}
                onOpenChange={(o) => !o && setDeleting(null)}
            >
                <AlertDialogContent
                    className="mono"
                    style={{
                        background: "var(--bg-elev)",
                        border: "1px solid var(--border-strong)",
                        color: "var(--text)",
                    }}
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle
                            className="mono text-sm uppercase tracking-[0.18em]"
                            style={{ color: "var(--danger)" }}
                        >
                            delete channel
                        </AlertDialogTitle>
                        <AlertDialogDescription
                            className="mono text-[11px]"
                            style={{ color: "var(--text-dim)" }}
                        >
                            {deleting?.name
                                ? `Delete '${deleting.name}'? Rules referencing it must be deleted first.`
                                : ""}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="ghost-btn">
                            cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            className="pill-btn"
                            style={{
                                background: "var(--danger)",
                                color: "#1a0610",
                                borderColor: "var(--danger)",
                            }}
                            data-testid="channel-delete-confirm"
                        >
                            delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}
